import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogPosts } from '../../mocks/blog';
import SiteNavbar from '../../components/feature/SiteNavbar';
import FooterSection from '../home/components/FooterSection';
import ArticleHero from './components/ArticleHero';
import ArticleBody from './components/ArticleBody';
import ArticleSidebar from './components/ArticleSidebar';
import ArticleRelated from './components/ArticleRelated';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);

  const post = blogPosts.find((p) => p.id === Number(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!post) navigate('/blog');
  }, [post, navigate]);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = doc.scrollTop || document.body.scrollTop;
      const max = doc.scrollHeight - doc.clientHeight;
      setProgress(max > 0 ? (scrolled / max) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!post) return null;

  const related = [
    ...blogPosts.filter((p) => p.id !== post.id && p.category === post.category),
    ...blogPosts.filter((p) => p.id !== post.id && p.category !== post.category),
  ].slice(0, 3);

  return (
    <div className="bg-zinc-950 min-h-screen">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-zinc-800/80">
        <div
          className="h-full bg-gradient-to-r from-orange-500 to-amber-400 transition-all duration-75"
          style={{ width: `${progress}%` }}
        />
      </div>

      <SiteNavbar />
      <ArticleHero post={post} />

      {/* Article content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex gap-14 items-start">
          {/* Main content */}
          <div className="flex-1 min-w-0 max-w-3xl">
            <ArticleBody post={post} />
          </div>

          {/* Sidebar */}
          <div className="hidden xl:block w-72 flex-shrink-0">
            <ArticleSidebar post={post} />
          </div>
        </div>
      </div>

      {/* Mobile sidebar author card */}
      <div className="xl:hidden max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-orange-500/30 flex-shrink-0">
            <img src={post.authorImage} alt={post.author} className="w-full h-full object-cover object-top" />
          </div>
          <div>
            <p className="text-white font-bold text-sm">{post.author}</p>
            <p className="text-zinc-500 text-xs mt-0.5">{post.authorRole}</p>
            <p className="text-zinc-400 text-xs mt-1">{post.date} &middot; {post.readTime}</p>
          </div>
        </div>
      </div>

      <ArticleRelated posts={related} />
      <FooterSection />
    </div>
  );
}
